import React, { useCallback, useMemo, useState } from "react";
import styled from "styled-components";

import {
  useGetRolesQuery,
  useGetRoleTagsQuery,
  useGetRoleDetailQuery,
  useDeleteRoleMutation,
  RoleListItem,
} from "../../../app/services/roles";
import { roleStatusLabel } from "../../../components/offer-wizard/roleAcceptPosition";
import StatusIndicator from "../../../components/status-indicator/StatusIndicator";
import StatusBadge from "../../../components/status-badge/StatusBadge";
import IconButton from "../../../components/button/IconButton";
import ContentTag from "../../../components/content-tag/ContentTag";
import FilterChipBar from "../../../components/filter-chip/FilterChipBar";
import type { ShotTag } from "../../../app/services/projects";
import { roleTagToShotTag } from "../../../utils/roleTagAdapter";

import RoleEditModal from "./RoleEditModal";
import RoleThumbnail from "./RoleThumbnail";
import ConfirmModal from "../../../components/modal/ConfirmModal";
import FullWidthAddBlock from "./FullWidthAddBlock";
import { useStatusIndicator } from "../hooks/useStatusIndicator";

/**
 * 編集モーダルの開閉状態。
 * - `null`: 閉じている
 * - `{ mode: "create" }`: 新規作成モード
 * - `{ mode: "edit"; roleId }`: 既存ロールの編集モード
 */
type EditingState =
  | null
  | { mode: "create" }
  | { mode: "edit"; roleId: number };

const HelpText = styled.p.attrs({ className: "bodytext-13" })`
  color: var(--soremo-light-gray);
`;

const ToolbarRow = styled.div.attrs({
  className: "u-row u-w100 u-gap16 u-align-center u-justify-between",
})`
  flex-wrap: wrap;
`;

const RolesList = styled.ul.attrs({ className: "u-col u-w100" })`
  gap: 8px;
  list-style: none;
  padding: 0;
  margin: 0;
`;

const RoleRow = styled.li.attrs({
  className: "u-row u-w100 u-gap12 u-align-center",
})`
  border: 1px solid var(--soremo-border);
  border-radius: 6px;
  padding: 12px 16px;
  background: #fff;
  cursor: pointer;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      border-color: var(--soremo-blue);
    }
  }
`;

const RoleSummary = styled.div.attrs({ className: "u-col u-fill u-gap4" })``;

const RoleTitleRow = styled.div.attrs({
  className: "u-row u-gap8 u-align-center",
})`
  flex-wrap: wrap;
`;

const RoleId = styled.span.attrs({ className: "bodytext-11" })`
  color: var(--soremo-light-gray);
  font-family: "din-2014", sans-serif;
  font-weight: 800;
`;

const Tags = styled.div.attrs({ className: "u-row u-gap4" })`
  flex-wrap: wrap;
`;

const Description = styled.p.attrs({ className: "bodytext-13" })`
  color: var(--soremo-text);
`;

const RowActions = styled.div.attrs({ className: "u-row u-gap12 u-align-center" })``;

const RolesManager: React.FC = () => {
  const { data: tagsData } = useGetRoleTagsQuery();
  const [selectedTagIdStr, setSelectedTagIdStr] = useState<string | null>(null);

  const selectedTagId = useMemo(
    () =>
      selectedTagIdStr === null
        ? undefined
        : Number.parseInt(selectedTagIdStr, 10),
    [selectedTagIdStr],
  );

  const { data: rolesData, isFetching } = useGetRolesQuery({
    tag_id: selectedTagId,
    limit: 200,
  });

  const [deleteRole] = useDeleteRoleMutation();

  const [editing, setEditing] = useState<EditingState>(null);
  const [pendingDelete, setPendingDelete] = useState<RoleListItem | null>(null);
  const { status, indicate } = useStatusIndicator();

  const tagsForBar: ShotTag[] = useMemo(
    () => (tagsData?.data?.tags ?? []).map(roleTagToShotTag),
    [tagsData],
  );
  const tagNameById = useMemo(() => {
    const map = new Map<number, string>();
    (tagsData?.data?.tags ?? []).forEach((t) => map.set(t.id, t.name));
    return map;
  }, [tagsData]);

  const roles: RoleListItem[] = useMemo(
    () => rolesData?.data?.roles ?? [],
    [rolesData],
  );

  const handleDelete = useCallback(async () => {
    if (!pendingDelete) return;
    const target = pendingDelete;
    setPendingDelete(null);
    try {
      indicate("deleting");
      await deleteRole(target.id).unwrap();
      indicate("saved");
    } catch (e) {
      console.error("Failed to delete role:", e);
      indicate("error", 3000);
    }
  }, [deleteRole, pendingDelete, indicate]);

  return (
    <>
      <div className="u-col u-w100 u-gap16">
        <HelpText>
          ロール（accounts_role）の作成・編集・削除を行います。削除すると、関連する CreatorRole / RoleTagAssignment も自動的に消えます（cascade）。
        </HelpText>

        <ToolbarRow>
          <FilterChipBar
            items={tagsForBar}
            selectedId={selectedTagIdStr}
            onSelect={setSelectedTagIdStr}
          />
        </ToolbarRow>

        {isFetching && roles.length === 0 ? (
          <p className="bodytext-13 u-text-light-gray">読み込み中...</p>
        ) : roles.length === 0 ? (
          <p className="bodytext-13 u-text-light-gray">
            該当するロールがありません。
          </p>
        ) : (
          <RolesList>
            {roles.map((role) => {
              const statusLabel = roleStatusLabel(
                role.accept_position,
                role.allow_subcontracting,
              );
              return (
                <RoleRow
                  key={role.id}
                  onClick={() =>
                    setEditing({ mode: "edit", roleId: role.id })
                  }
                >
                  <RoleThumbnail roleId={role.id} size={56} />
                  <RoleSummary>
                    <RoleTitleRow>
                      <RoleId>#{role.id}</RoleId>
                      <span className="heading-13">{role.role_name_jp}</span>
                      {role.role_name_en && (
                        <span className="bodytext-11 u-text-light-gray">
                          {role.role_name_en}
                        </span>
                      )}
                      {statusLabel && (
                        <StatusBadge
                          variant="inline"
                          label={statusLabel}
                          highlighted
                        />
                      )}
                    </RoleTitleRow>
                    {role.description && (
                      <Description>{role.description}</Description>
                    )}
                    <Tags>
                      {role.tags.map((tagId) => (
                        <ContentTag key={tagId}>
                          {tagNameById.get(tagId) ?? `#${tagId}`}
                        </ContentTag>
                      ))}
                    </Tags>
                  </RoleSummary>
                  <RowActions onClick={(e) => e.stopPropagation()}>
                    <IconButton onClick={() => setPendingDelete(role)} icon="delete" />
                  </RowActions>
                </RoleRow>
              );
            })}
          </RolesList>
        )}

        <FullWidthAddBlock
          label="ロールを追加"
          onClick={() => setEditing({ mode: "create" })}
        />
      </div>

      {editing && (
        <RoleEditModalGate
          editing={editing}
          onClose={() => setEditing(null)}
          onSaved={() => indicate("saved")}
        />
      )}

      {pendingDelete && (
        <ConfirmModal
          message={`「${pendingDelete.role_name_jp}」(#${pendingDelete.id}) を削除しますか？\nこのロールに紐付いている CreatorRole / RoleTagAssignment も cascade で削除されます。この操作は元に戻せません。`}
          confirmLabel="削除"
          onConfirm={handleDelete}
          onCancel={() => setPendingDelete(null)}
        />
      )}

      <StatusIndicator status={status} withBottomAppBar={false} />
    </>
  );
};

interface RoleEditModalGateProps {
  /** 開いているモーダルの種別。`null` の場合は呼び出し側で描画しない。 */
  editing: { mode: "create" } | { mode: "edit"; roleId: number };
  onClose: () => void;
  onSaved: () => void;
}

/**
 * 編集モーダル表示の Gate。
 * - `mode === "create"`: 詳細を取得せずに即モーダルを開く。
 * - `mode === "edit"`: 詳細を取得し終わるまで待ってから初期値を渡してモーダルを開く。
 *   フォーム初期値の確定を保証するため、未取得の間は何も描画しない。
 */
const RoleEditModalGate: React.FC<RoleEditModalGateProps> = ({
  editing,
  onClose,
  onSaved,
}) => {
  const skip = editing.mode === "create";
  const { data, isLoading } = useGetRoleDetailQuery(
    skip ? 0 : editing.roleId,
    { skip },
  );

  if (editing.mode === "edit" && (isLoading || !data)) {
    return null;
  }

  return (
    <RoleEditModal
      mode={editing.mode}
      initialRole={editing.mode === "edit" ? data?.data.role : undefined}
      onClose={onClose}
      onSaved={onSaved}
    />
  );
};

export default RolesManager;
